import {useCallback, useRef} from 'react';

import {assertAndContinue} from 'Asserts';

export interface ScrollDelta {
  deltaLeft: number;
  deltaTop: number;
}

export interface ScrollPosition {
  scrollLeft: number;
  scrollTop: number;
}

export interface UseDnDScrollHandlerInput {
  setScrollDelta: (scrollDelta: ScrollDelta) => void;
}

export interface UseDnDScrollHandlerOutput {
  handleScroll: (e: Event) => void;
}

export function eventToScrollPosition(event: Event): ScrollPosition | null {
  // We can either scroll an element or the document. If we scroll an element, srcElement will have
  // scrollLeft and scrollTop, and if we scroll the document, scrElement will instead have a scrollingElement
  // object, which has the scrollLeft and scrollTop.
  //
  // We handle both because element is useful in contexts where the DnDCanvas is in another component such
  // as AnalyticsDashboardEditor, and Document is useful in contexts where DnDCanvas is the base element
  // such as DndCanvas.stories.
  if (event.srcElement == null) {
    assertAndContinue(false, 'Expected scroll event.srcElement to be non-null on Element scroll events');
    return null;
  } else if ((event.srcElement as HTMLElement).scrollLeft !== undefined) {
    return {
      scrollLeft: (event.srcElement as HTMLElement).scrollLeft,
      scrollTop: (event.srcElement as HTMLElement).scrollTop,
    };
  } else {
    const scrollingElement = (event.srcElement as Document).scrollingElement;

    if (scrollingElement == null) {
      assertAndContinue(
        false,
        'Expected scroll event.srcElement.scrollingElement to be non-null on Document scroll events'
      );
      return null;
    } else {
      return {
        scrollLeft: scrollingElement.scrollLeft,
        scrollTop: scrollingElement.scrollTop,
      };
    }
  }
}

export default function useDnDScrollHandler({
  setScrollDelta,
}: UseDnDScrollHandlerInput): UseDnDScrollHandlerOutput {
  const previousScrollPosition = useRef({scrollLeft: 0, scrollTop: 0});
  const handleScroll = useCallback(
    (event: Event) => {
      const scrollPosition = eventToScrollPosition(event);

      if (scrollPosition == null) {
        return;
      }

      // To calculate the accurate dragDelta, which is the position of a dragged block relative to the
      // page/component while dragging, the parent hook depends on this hook sending vectors explaining how the
      // page or element has scrolled as opposed to it's actual position. To do this, we essentially keep track
      // of the current and previous scroll position positions, and calculate the difference with the current
      // scroll position.
      const scrollDelta = {
        deltaLeft: scrollPosition.scrollLeft - previousScrollPosition.current.scrollLeft,
        deltaTop: scrollPosition.scrollTop - previousScrollPosition.current.scrollTop,
      };

      setScrollDelta(scrollDelta);

      previousScrollPosition.current = scrollPosition;
    },
    [setScrollDelta]
  );
  return {
    handleScroll,
  };
}
